import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

import useIsCurrentPath from "../../hooks/useIsCurrentPath";
import useIframeDetection from "../../hooks/useIframeDetection";

import rali from "../../assets/images/logo-rali.svg";
import rali2 from "../../assets/images/logo-rali-i.svg";
import loco from "../../assets/images/image1.svg";
//import pulso from "../../assets/images/image2.svg";

import { Link } from "react-router-dom";
import { Star, ThumbsUp, BarChart3, TrendingUp, Vote, Shield } from "lucide-react";

const navItems = [
  { name: "Eleição 2026", path: "/" },
  { name: "Avaliação de Governo", path: "/avaliacao" },
  { name: "Aprovação do Governo", path: "/aprovacao" },
  { name: "Comparativo Histórico", path: "/comparativo" },
  { name: "Índices Econômicos", path: "/indices" },
];

// Mobile nav items - will be filtered based on iframe detection
const baseMobileNavItems = [
  { path: "/", icon: Vote },
  { path: "/avaliacao", icon: Star },
  { path: "/aprovacao", icon: ThumbsUp },
  { path: "/comparativo", icon: BarChart3 },
  { path: "/indices", icon: TrendingUp },
  { path: "/login", icon: Shield, adminOnly: true },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const linksRef = useRef();
  const [underline, setUnderline] = useState({ left: 0, width: 0 });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigationTimeoutRef = useRef(null);
  const pendingPathRef = useRef(null);
  
  // Detect if running in iframe - if true, hide all admin features
  const { isInIframe } = useIframeDetection();

  // Force navigation handler - if navigation doesn't happen in 500ms, force reload
  const handleForcedNavigation = (path) => {
    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // If we're already on this path, don't do anything
    if (location.pathname === path) {
      return;
    }

    // Store the pending path
    pendingPathRef.current = path;

    // Set timeout to force reload if navigation doesn't happen
    navigationTimeoutRef.current = setTimeout(() => {
      if (pendingPathRef.current && location.pathname !== pendingPathRef.current) {
        window.location.href = pendingPathRef.current;
      }
    }, 500);

    // Try normal navigation
    try {
      navigate(path);
    } catch (error) {
      window.location.href = path;
    }
  };

  // Clear timeout when location changes successfully
  useEffect(() => {
    if (navigationTimeoutRef.current && pendingPathRef.current === location.pathname) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
      pendingPathRef.current = null;
    }
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const activeLink = linksRef.current.querySelector(".nav-link.active");
    if (activeLink) {
      const { offsetLeft, offsetWidth } = activeLink;
      setUnderline({ left: offsetLeft, width: offsetWidth });
    }
  }, [location]);

  // In iframes and mobile, always use compact navbar to save vertical space
  useEffect(() => {
    const isMobile = window.innerWidth <= 1074; // Mobile breakpoint from CSS
    
    // If in iframe or mobile, always use scrolled (compact) state
    if (isInIframe || isMobile) {
      setIsScrolled(true);
      
      // On mobile, still listen for resize in case user rotates device
      if (isMobile && !isInIframe) {
        const handleResize = () => {
          const newIsMobile = window.innerWidth <= 1074;
          setIsScrolled(newIsMobile || window.scrollY > 20);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }
      
      return; // No scroll listener needed in iframe
    }

    // For desktop pages, detect scroll
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInIframe]);

  const isLoginPage = useIsCurrentPath("/login");
  const isAdminPage = useIsCurrentPath("/dashboard");

  // Filter mobile nav items - remove admin icon if in iframe
  const mobileNavItems = baseMobileNavItems.filter(item => {
    if (isInIframe && item.adminOnly) {
      return false;
    }
    return true;
  });

  return (
    <nav className={`layout-container navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
      <Link 
        className="container-logos" 
        to="/"
        onClick={(e) => {
          e.preventDefault();
          handleForcedNavigation("/");
        }}
      >
        <img src={rali} alt="Logo Rali" />
        <img src={rali2} alt="Logo Rali" />
        <span>+</span>
        <img src={loco} alt="Logo Instituto Locomotiva" />
      </Link>

      <div className="nav-links" ref={linksRef}>
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              handleForcedNavigation(item.path);
            }}
          >
            {item.name}
          </NavLink>
        ))}
        <div
          className="underline"
          style={{
            left: `${underline.left}px`,
            width: `${underline.width}px`,
          }}
        />
      </div>

      <div className="nav-mobile-icons">
        {mobileNavItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className="nav-mobile-icon"
              onClick={(e) => {
                e.preventDefault();
                handleForcedNavigation(item.path);
              }}
            >
              <IconComponent size={22} />
            </NavLink>
          );
        })}
      </div>

      {/* Only show admin button if NOT in iframe */}
      {!isInIframe && (
        <Link 
          to="/login" 
          className={`button-admin ${isLoginPage || isAdminPage ? "button-admin-active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            handleForcedNavigation("/login");
          }}
        >
          Administrador
        </Link>
      )}
    </nav>
  );
}
